import React, { ChangeEvent, useEffect, useState } from "react";
import { Box, Checkbox, FormControlLabel, IconButton, SelectChangeEvent, Stack, TextField} from "@mui/material";
import { IExpression, IDeliveryType } from "../../../models/configuration/plugin/IConfiguration";
import ExpressionEditor from "../../shared/config/ExpressionEditor";
import { IExpressionEvaluation } from "../../../models/IExpressionValidation";
import EditIcon from '@mui/icons-material/Edit';
import { DtLabel } from "../../ui/label";
import { useHostDetails } from "../../../hooks/useHostDetails";

interface DeliveryTypeDetailsProps {
    selectedDeliveryType: IDeliveryType | null;
    handleOnEnabledChange: (event: ChangeEvent<HTMLInputElement>) => void;
    handleOnEnabledForTpoUserChange: (event: ChangeEvent<HTMLInputElement>) => void;
    handleOnOrderChange: (event: SelectChangeEvent) => void;
    handleOnCaptionChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleOnToolTipTextChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleOnAutoSelectChange: (event: ChangeEvent<HTMLInputElement>) => void;
    autoSelect: boolean; 
    autoSelectExpression: IExpression;
    setAutoSelect: (enabled: boolean) => void; 
    setAutoSelectExpression: (expr: IExpression) => void;
}

const DeliveryTypeDetails: React.FC<DeliveryTypeDetailsProps> =
    ({
        selectedDeliveryType: selectedType,
        handleOnEnabledChange,
        handleOnEnabledForTpoUserChange,
        handleOnCaptionChange,
        handleOnToolTipTextChange,
        handleOnAutoSelectChange,
        autoSelect,
        autoSelectExpression,
        setAutoSelect,
        setAutoSelectExpression
    }) => {
        const [autoSelectExpressionOpen, setAutoSelectExpressionOpen] = useState(false);
        const { host } = useHostDetails();

        useEffect(() => {
            if (selectedType) {
                setAutoSelectExpression({
                    ...autoSelectExpression,
                    ExpressionString: selectedType.AutoSelectWhen ?? ""
                });
                setAutoSelect(!!selectedType.AutoSelectWhen);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [selectedType]);

        const handleAutoSelectOpen = (_source: string) => setAutoSelectExpressionOpen(true);
        const handleAutoSelectClose = () => setAutoSelectExpressionOpen(false);

        // Updates the Check Box and the Auto Select Expression
        const handleValidateExpression = (validationResult: IExpressionEvaluation) => {
            if (validationResult && !validationResult.error) setAutoSelect(true);
        };

        const updateExpressions = (expression: IExpression, _mode: string): string | null => {
            if (expression) {
                handleOnAutoSelectChange({ target: { value: expression.ExpressionString } } as React.ChangeEvent<HTMLInputElement>);
                setAutoSelectExpression(expression);
                setAutoSelectExpressionOpen(false);
            }
            return null;
        };

        return (
            <Box>
                {/* First row: text fields */}
                <Stack direction="row" spacing={2} sx={{ mb: 1, pt: 2 }}>
                    <TextField
                        className="caption-field"
                        onChange={(e) => handleOnCaptionChange(e)}
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={selectedType?.Caption ?? ""}
                        label="Caption"
                    />
                    <TextField
                        className="tooltip-field"
                        onChange={(e) => handleOnToolTipTextChange(e)}
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={selectedType?.ToolTipText ?? ""}
                        label="ToolTip Text"
                    />
                </Stack>
                {/* Second row: checkboxes */}
                <Stack direction="row" spacing={1}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                onChange={handleOnEnabledChange}
                                checked={selectedType?.Enabled ?? false}
                            />
                        }
                        label={<DtLabel size="large" color="text.primary" sx={{mt: 0.5}}>Enabled</DtLabel>}
                    />

                    {host.isTPOEnabled && <FormControlLabel
                        control={
                            <Checkbox
                                onChange={handleOnEnabledForTpoUserChange}
                                checked={selectedType?.EnableForTpoUser ?? false}
                            />
                        }
                        label={<DtLabel size="large" color="text.primary" sx={{ mt: 0.5 }}>Enable TPO</DtLabel>}
                    />}
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    onChange={() => handleAutoSelectOpen('edit')}
                                    checked={autoSelect ?? false}
                                />
                            }
                            label={<DtLabel size="large" color="text.primary" sx={{mt: 0.5}}>Auto-Select</DtLabel>}
                        />
                        {autoSelect && (
                            <IconButton
                                color="primary"
                                onClick={() => handleAutoSelectOpen('edit')}
                                size="small"
                                title="Edit Auto-Select Rule"
                                sx={{ ml: -1 }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Stack>

                <ExpressionEditor
                    editorTitle="Setup Auto Select Rule"
                    expression={autoSelectExpression}
                    onValidate={handleValidateExpression}
                    onSave={updateExpressions}
                    renderMode='dialog'
                    editorMode='add'
                    open={autoSelectExpressionOpen}
                    onCloseDialog={handleAutoSelectClose}
                />
            </Box>
        );
    }

export default DeliveryTypeDetails;